import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
  type ComponentType, type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight } from 'lucide-react';
import { R, label, shadow } from '../lib/ui';
import type { IconProps } from './Primitives';

export type MenuEntry =
  | { type: 'divider' }
  | { type: 'header'; label: string }
  | {
      label: string;
      icon?: ComponentType<IconProps>;
      shortcut?: string;
      danger?: boolean;
      disabled?: boolean;
      onClick?: () => void;
      submenu?: MenuEntry[];
    };

/**
 * Меню по правому клику. Возвращает open для обработчика onContextMenu
 * и Portal, который монтируется в document.body для исключения проблем с overflow и scroll.
 */
export function useContextMenu() {
  const [state, setState] = useState<{ id: number; x: number; y: number; items: MenuEntry[] } | null>(null);
  const nextId = useRef(0);

  const open = useCallback((e: ReactMouseEvent, items: MenuEntry[]) => {
    e.preventDefault();
    e.stopPropagation();
    setState({ id: ++nextId.current, x: e.clientX, y: e.clientY, items });
  }, []);

  const openAt = useCallback((x: number, y: number, items: MenuEntry[]) => {
    setState({ id: ++nextId.current, x, y, items });
  }, []);

  const close = useCallback(() => setState(null), []);

  const Portal = useCallback(
    () =>
      state && typeof document !== 'undefined'
        ? createPortal(
            <ContextMenu
              key={state.id}
              x={state.x}
              y={state.y}
              items={state.items}
              onClose={close}
            />,
            document.body,
          )
        : null,
    [state, close],
  );

  return { open, openAt, close, Portal };
}

function ContextMenu({
  x, y, items, onClose,
}: {
  x: number;
  y: number;
  items: MenuEntry[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(() => {
    const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
    return {
      x: Math.max(8, Math.min(x, winW - 240)),
      y: Math.max(8, Math.min(y, winH - 320)),
    };
  });

  // Меню не должно вылезать за экран — двигаем его внутрь.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    let nextX = x;
    let nextY = y;

    if (nextX + rect.width > window.innerWidth - 8) {
      nextX = window.innerWidth - rect.width - 8;
    }
    if (nextY + rect.height > window.innerHeight - 8) {
      nextY = window.innerHeight - rect.height - 8;
    }
    setPos({ x: Math.max(8, nextX), y: Math.max(8, nextY) });
  }, [x, y]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      // Если клик внутри меню или любого портального подменю — не закрываем здесь,
      // клик должен нормально выполниться кнопкой пункта меню.
      if (target && target.closest('[data-context-menu="true"]')) {
        return;
      }
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      data-context-menu="true"
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'fixed',
        top: pos.y,
        left: pos.x,
        zIndex: 9999,
        minWidth: 220,
        maxHeight: 'calc(100vh - 16px)',
        overflowY: 'auto',
        background: R.bgPanel,
        border: `2px solid ${R.border}`,
        boxShadow: shadow.dropdown,
        padding: '6px 0',
        animation: 'rtIn .1s ease-out',
      }}
    >
      <MenuList items={items} onSelect={onClose} />
    </div>
  );
}

function Submenu({
  anchorEl,
  items,
  onSelect,
  onMouseEnter,
  onMouseLeave,
}: {
  anchorEl: HTMLElement;
  items: MenuEntry[];
  onSelect: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState(() => {
    const itemRect = anchorEl.getBoundingClientRect();
    const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
    const approxWidth = 220;
    const approxHeight = Math.min(items.length * 36 + 12, winH - 32);

    // Если подменю справа не влезает в экран (например, колонка у правого края),
    // открываем его СЛЕВА от основного меню!
    const fitsRight = itemRect.right + approxWidth + 8 <= winW;
    let initialX = fitsRight ? itemRect.right + 2 : itemRect.left - approxWidth - 2;
    if (initialX < 8) initialX = 8;
    if (initialX + approxWidth > winW - 8) initialX = winW - approxWidth - 8;

    let initialY = itemRect.top - 4;
    if (initialY + approxHeight > winH - 8) {
      initialY = winH - approxHeight - 8;
    }
    if (initialY < 8) initialY = 8;

    return { x: initialX, y: initialY };
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const itemRect = anchorEl.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    const winW = window.innerWidth;
    const winH = window.innerHeight;

    // Проверяем, помещается ли справа
    const fitsRight = itemRect.right + rect.width + 8 <= winW;
    let nextX = fitsRight ? itemRect.right + 2 : itemRect.left - rect.width - 2;

    if (nextX < 8) nextX = 8;
    if (nextX + rect.width > winW - 8) {
      nextX = winW - rect.width - 8;
    }

    let nextY = itemRect.top - 4;
    if (nextY + rect.height > winH - 8) {
      nextY = winH - rect.height - 8;
    }
    if (nextY < 8) nextY = 8;

    setPos({ x: nextX, y: nextY });
  }, [anchorEl]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      data-context-menu="true"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        position: 'fixed',
        top: pos.y,
        left: pos.x,
        minWidth: 210,
        maxHeight: 'calc(100vh - 24px)',
        overflowY: 'auto',
        background: R.bgPanel,
        border: `2px solid ${R.border}`,
        boxShadow: shadow.dropdown,
        padding: '6px 0',
        zIndex: 10001,
        animation: 'rtIn .08s ease-out',
      }}
    >
      {items.map((sub, k) => {
        if ('type' in sub && sub.type === 'divider') {
          return <div key={k} style={{ height: 1, background: R.grid, margin: '6px 0' }} />;
        }
        if ('type' in sub && sub.type === 'header') {
          return (
            <div key={k} style={{ ...label, padding: '6px 12px 8px' }}>
              {sub.label}
            </div>
          );
        }

        const item = sub;
        const color = item.disabled ? R.textMuted : item.danger ? R.danger : R.text;

        return (
          <button
            key={k}
            type="button"
            disabled={item.disabled}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (item.disabled) return;
              item.onClick?.();
              onSelect();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              padding: '7px 12px',
              textAlign: 'left',
              fontSize: 13,
              color,
              cursor: item.disabled ? 'default' : 'pointer',
              background: 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!item.disabled) e.currentTarget.style.background = R.bgElevated;
            }}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            {item.icon && <item.icon size={14} />}
            <span style={{ flex: 1 }}>{item.label}</span>
            {item.shortcut && (
              <span style={{ fontSize: 11.5, color: R.textMuted }}>{item.shortcut}</span>
            )}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}

/** Отрисовка пунктов — общая для контекстного меню и меню документа. */
export function MenuList({
  items, onSelect,
}: {
  items: MenuEntry[];
  onSelect: () => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const closeTimer = useRef<number | null>(null);

  const handleItemEnter = (i: number) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setHovered(i);
  };

  const handleItemLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setHovered(null);
    }, 200);
  };

  const handleSubmenuEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleSubmenuLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setHovered(null);
    }, 200);
  };

  return (
    <>
      {items.map((entry, i) => {
        if ('type' in entry && entry.type === 'divider') {
          return <div key={i} style={{ height: 1, background: R.grid, margin: '6px 0' }} />;
        }

        if ('type' in entry && entry.type === 'header') {
          return (
            <div key={i} style={{ ...label, padding: '6px 12px 8px' }}>
              {entry.label}
            </div>
          );
        }

        const item = entry;
        const active = hovered === i;
        const color = item.disabled ? R.textMuted : item.danger ? R.danger : R.text;

        return (
          <div
            key={i}
            ref={(el) => {
              if (el) itemRefs.current.set(i, el);
              else itemRefs.current.delete(i);
            }}
            style={{ position: 'relative' }}
            onMouseEnter={() => handleItemEnter(i)}
            onMouseLeave={handleItemLeave}
          >
            <button
              type="button"
              disabled={item.disabled}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (item.disabled) return;
                if (item.submenu) {
                  if (closeTimer.current) clearTimeout(closeTimer.current);
                  setHovered(active ? null : i);
                  return;
                }
                item.onClick?.();
                onSelect();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '7px 12px',
                textAlign: 'left',
                fontSize: 13,
                color,
                cursor: item.disabled ? 'default' : 'pointer',
                background: active && !item.disabled ? R.bgElevated : 'transparent',
              }}
            >
              {item.icon && <item.icon size={14} />}
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.shortcut && (
                <span style={{ fontSize: 11.5, color: R.textMuted }}>{item.shortcut}</span>
              )}
              {item.submenu && <ChevronRight size={13} color={R.textMuted} />}
            </button>

            {item.submenu && active && itemRefs.current.get(i) && (
              <Submenu
                anchorEl={itemRefs.current.get(i)!}
                items={item.submenu}
                onSelect={onSelect}
                onMouseEnter={handleSubmenuEnter}
                onMouseLeave={handleSubmenuLeave}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
